import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { CONFIG } from '~/config';
import { ExternalRestClient } from '~/modules/requests';
import { RootState } from '~/types';
import {
  getItineraryOrder,
  getItineraryOrderFailure,
  getItineraryOrderSuccess,
  requestItineraryPayment,
  requestItineraryPaymentFailure,
  requestItineraryPaymentSuccess,
  setSelectedPackageId,
  startWizard,
} from '~/store/slices/itinerary-wizard';

function* onStartWizard({ payload }: PayloadAction<{ idempotencyKey: string; packageId?: string }>) {
  // ensure selected package id is set in state
  if (payload.packageId) {
    yield put(setSelectedPackageId(payload.packageId));
  }
}

function* onRequestItineraryPayment() {
  try {
    const itineraryWizard: RootState['itineraryWizard'] = yield select((state: RootState) => state.itineraryWizard);
    const bookingDetails = itineraryWizard?.bookingDetails;

    if (!bookingDetails) {
      yield put(requestItineraryPaymentFailure({ errorMessage: 'Missing itinerary details for payment request.' }));
      return;
    }

    const sourceSearchParams = bookingDetails.searchParams || {};
    const travelerSummary = sourceSearchParams.passengers || {};
    const travelers = Number(travelerSummary.adults || 0) + Number(travelerSummary.children || 0) + Number(travelerSummary.infants || 0);
    const searchParams = {
      origin: sourceSearchParams.origin || sourceSearchParams.from?.iata || sourceSearchParams.from?.city || sourceSearchParams.from?.title,
      destination: sourceSearchParams.destination || sourceSearchParams.to?.iata || sourceSearchParams.to?.city || sourceSearchParams.to?.title,
      originDetails: sourceSearchParams.originDetails || sourceSearchParams.from,
      destinationDetails: sourceSearchParams.destinationDetails || sourceSearchParams.to,
      departureDate: format(new Date(sourceSearchParams.departureDate), 'yyyy-MM-dd'),
      returnDate: sourceSearchParams.type === 'return' && sourceSearchParams.returnDate ? format(new Date(sourceSearchParams.returnDate), 'yyyy-MM-dd') : undefined,
      travelers: sourceSearchParams.travelers || travelers,
      currency: sourceSearchParams.currency || sourceSearchParams.currencyCode,
    };

    const response: { status: number; data: Record<string, any> } = yield call(
      [ExternalRestClient, ExternalRestClient.post],
      `${CONFIG.env.apiBaseUrl}v2/itinerary/orders/payment`,
      {
        host: window.location.origin,
        idempotencyKey: itineraryWizard?.idempotencyKey,
        packageId: itineraryWizard?.selectedPackageId,
        ...bookingDetails,
        searchParams,
      },
    );

    if (response.status === 200 && response.data) {
      yield put(requestItineraryPaymentSuccess(response.data));
      const orderId = response.data.payment?.orderId || response.data.orderId;
      if (orderId) {
        window.history.replaceState(window.history.state, '', `/itinerary/${orderId}`);
        try {
          window.localStorage.setItem('itinerary_order_number', orderId);
        } catch (error) {
          // localStorage is optional; the canonical ID remains in Redux and the URL.
        }
      }
      return;
    }

    yield put(requestItineraryPaymentFailure({ errorMessage: response?.data?.error?.message || response?.data?.error || 'Failed to request itinerary payment.' }));
  } catch (error: any) {
    yield put(requestItineraryPaymentFailure({ errorMessage: error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to request itinerary payment.' }));
  }
}

function getItineraryOrderById(orderId: string) {
  return ExternalRestClient.get(`${CONFIG.env.apiBaseUrl}v2/itinerary/orders/${orderId}`);
}

function delayEffect(duration: number) {
  return new Promise(resolve => setTimeout(resolve, duration));
}

function getOrderPaymentStatus(responseData: Record<string, any>): string {
  return String(responseData?.order?.paymentStatus || responseData?.paymentStatus || '').toLowerCase();
}

function* onGetItineraryOrder({ payload }: PayloadAction<{ orderId: string }>) {
  const maxRetryTime = 10000;
  const delay = 2000;
  let totalTime = 0;
  let lastResponseData: Record<string, any> | null = null;

  try {
    while (totalTime <= maxRetryTime) {
      try {
        const response: { status: number; data: Record<string, any> } = yield call(getItineraryOrderById, payload.orderId);

        if (response.status === 200 && response.data) {
          lastResponseData = response.data;

          if (getOrderPaymentStatus(response.data) !== 'pending') {
            yield put(getItineraryOrderSuccess(response.data));
            return;
          }

          yield call(delayEffect, delay);
          totalTime += delay;
          continue;
        }

        if (response.status !== 404) {
          yield put(getItineraryOrderFailure({ errorMessage: response?.data?.error || 'Failed to load itinerary order.' }));
          return;
        }
      } catch (error: any) {
        if (error?.response?.status !== 404) {
          yield put(getItineraryOrderFailure({ errorMessage: error?.response?.data?.error || 'Failed to load itinerary order.' }));
          return;
        }
      }

      yield call(delayEffect, delay);
      totalTime += delay;
    }

    if (lastResponseData) {
      yield put(getItineraryOrderSuccess(lastResponseData));
      return;
    }

    yield put(getItineraryOrderFailure({ errorMessage: `We could not find your itinerary order. Please contact support with reference ${payload.orderId}.` }));
  } catch (error: any) {
    yield put(getItineraryOrderFailure({ errorMessage: error?.response?.data?.error || 'Failed to load itinerary order.' }));
  }
}

export default function* itineraryWizardSaga() {
  yield all([
    takeLatest(startWizard.type, onStartWizard),
    takeLatest(requestItineraryPayment.type, onRequestItineraryPayment),
    takeLatest(getItineraryOrder.type, onGetItineraryOrder),
  ]);
}
